import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import IconButton from 'LuxKit/Button/IconButton'
import LineBellIcon from 'LuxKit/Icons/line/LineBellIcon'
import Caption from 'LuxKit/Typography/Caption'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import useToggle from 'hooks/useToggle'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import HeaderNotificationItem from './HeaderNotificationItem'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { viewAllUserNotifications } from 'actions/UserNotificationActions'
import ModalContext from 'contexts/ModalContext'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import useUserNotifications from 'hooks/Notifications/useUserNotifications'

const StyledNotificationIconButton = styled(IconButton)`
  position: relative;
`

const RedDotOnSignUp = styled(Caption)`
  position: absolute;
  top: ${rem(2)};
  right: ${rem(2)};
  padding: ${rem(2)} ${rem(4)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.messaging.critical.normalBackground};
  color: ${props => props.theme.palette.messaging.critical.normalContrast};
`

const EmptyState = styled(Group)`
  padding: ${rem(64)} ${rem(20)};
`

function HeaderNotification() {
  const [isOpen, toggleMenu, _, off] = useToggle()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const intl = useIntl()

  const isMobileScreen = useScreenSizeOnly('mobile')
  const [notifications] = useUserNotifications()
  const newNotifications = useMemo(() => notifications.filter(item => !item.viewed).length, [notifications])
  const notificationCountDisplay = useMemo(() => newNotifications > 9 ? '9+' : String(newNotifications), [newNotifications])
  const buttonRef = useRef<HTMLButtonElement>(null)
  const isSubscribed = useAppSelector(state => state.leSubscriptions.settings.appPushSubscribed)

  const Content = useMemo(() => (
    <>
      {!notifications.length && <EmptyState direction="vertical" gap={8} horizontalAlign="center">
        <LineBellIcon />
        <Group direction="vertical" horizontalAlign="center">
          <BodyText variant="large" weight="bold">
            {intl.formatMessage({
              id: 'zKWJhsiR2q',
              defaultMessage: 'No new notifications',
              description: 'Empty state title when there are no notifications',
            })}
          </BodyText>
          {isSubscribed && <BodyText variant="medium">
            {intl.formatMessage({
              id: 'Nzzkezgsha',
              defaultMessage: 'We’ll let you know when updates arrive.',
              description: 'Empty state message when user is subscribed to push notifications',
            })}
          </BodyText>}
        </Group>
      </EmptyState>}
      {!!notifications.length && <Group direction="vertical">
        {notifications.map(notification => <HeaderNotificationItem
          key={notification.id}
          notification={notification}
        />)}
      </Group>}
    </>
  ), [intl, isSubscribed, notifications])

  const closeMenu = useCallback(() => {
    off()
    dispatch(viewAllUserNotifications())
  }, [dispatch, off])

  const onNotificationsClick = useCallback(async() => {
    if (isMobileScreen) {
      // Designers fudged the padding on the items, so we'll make the
      // the modal paddingless until we get it aligned properly
      await showModal(<ModalBase height="full">
        <ModalHeader
          title={intl.formatMessage({
            id: 'J5ooGMmuAm',
            defaultMessage: 'Notifications',
            description: 'Modal title for notifications',
          })}
        />
        <ModalBody>
          {Content}
        </ModalBody>
      </ModalBase>)
      closeMenu()
    } else {
      toggleMenu()
    }
  }, [Content, closeMenu, intl, isMobileScreen, showModal, toggleMenu])

  return (
    <>
      <StyledNotificationIconButton
        ref={buttonRef}
        kind="tertiary"
        variant="default"
        role="menuitem"
        onClick={onNotificationsClick}
      >
        <LineBellIcon />
        {!!newNotifications && <RedDotOnSignUp variant="small">
          {notificationCountDisplay}
        </RedDotOnSignUp>}
      </StyledNotificationIconButton>
      <DropdownList
        open={isOpen && !isMobileScreen}
        size="M"
        width="min"
        anchorRef={buttonRef}
        triggerRef={buttonRef}
        placement="bottom-end"
        onClose={closeMenu}
      >
        {Content}
      </DropdownList>
    </>
  )
}

export default HeaderNotification
